<script lang="ts" setup>
import { ref, computed, watch, nextTick, onMounted } from "vue";
import { useUserStore, PartyType } from "@/stores/user";
import { useAuthStore } from "@/stores/auth";
import { useAppConfigurationStore } from "@/stores/appconfig";
import router from "@/router/index";
import { logoInnax } from "@/services/images";
import CommandSearchMenu from "@/components/app/CommandSearchMenu.vue";
import StatusDialog from "@/components/general/StatusDialog.vue";
import { getStatusPageComponents, severityCode } from "@/services/api/info.api";

const drawerLeft = ref(false);
const drawerRight = ref(false);
const switchingParty = ref(false);
const projectSearch = ref("");
const projectMenuOpen = ref(false);
const projectSearchField = ref<any>(null);

const userStore = useUserStore();
const authStore = useAuthStore();
const appconfigStore = useAppConfigurationStore();

console.log("appbase appconfigStore", appconfigStore);

const showMenuItemBeheer = computed(() => userStore.isAdministrator || userStore.hasRoleManager);
const showMenuItemInformatieplicht = computed(() => userStore.isPartyUtility && userStore.hasRoleManager);
const showMenuItemInfo = computed(() => userStore.hasRoleManager);
const logoName = computed(() => appconfigStore.portalUrl + "api/asset/logo/" + userStore.currentPartyReference);
const projectList = computed(() => {
  const normalizedSearch = projectSearch.value.trim().toLowerCase();

  const items = userStore.getAllProjecs
    .map((el: any) => ({
      reference: el.partyReference,
      name: el.description,
      logoName: el.logoName,
      isManager: el.isManager,
      partyType: el.partyTypeKey,
      isCurrent: userStore.currentPartyReference === el.partyReference,
    }))
    .filter((el: any) => !normalizedSearch || el.name.toLowerCase().includes(normalizedSearch));

  const current = items.find((el) => el.isCurrent);
  const others = items.filter((el) => !el.isCurrent).sort(compareName);

  const sortedItems = current ? [current, ...others] : others;
  console.log("AppBase projectList ", userStore.getAllProjecs, items);
  return sortedItems;
});

const breadcrumbs = computed(() => {
  let list = userStore.breadcrumbList;
  return list;
});

const showGlobalBreadcrumb = computed(() => router.currentRoute.value.name !== "DMJOP dashboard");

function logout() {
  console.log("AppBase logout ");
  if (authStore.logoutDemo()) {
    router.push({ name: "Account" });
  } else {
    authStore.logout();
  }
}

function compareName(a: any, b: any): number {
  const itemA = a.name.toUpperCase();
  const itemB = b.name.toUpperCase();

  let comparison = 0;
  if (itemA > itemB) {
    comparison = 1;
  } else if (itemA < itemB) {
    comparison = -1;
  }
  return comparison;
}

async function switchProject(partyReference: string) {
  try {
    switchingParty.value = true;
    await router.push({ name: "Dashboard", params: { partyReference: partyReference } });
    console.log("%cAppBase switchProject, partyReference: ", "background: orange; color: black", partyReference);
    projectSearch.value = "";
    projectMenuOpen.value = false;
  } finally {
    switchingParty.value = false;
  }
}

const PARTY_TYPE_CONFIG: Record<string, { icon: string | null; tooltip: string }> = {
  [PartyType.EnergyLabels]: { icon: "mdi-label-outline", tooltip: "Energielabels" },
  [PartyType.Homes]: { icon: "mdi-home-outline", tooltip: "Woningen" },
  [PartyType.Utility]: { icon: "mdi-office-building-outline", tooltip: "Utiliteit" },
};

function partyIcon(partyType: string | number): string | null {
  return PARTY_TYPE_CONFIG[String(partyType)]?.icon ?? null;
}

function partyTypeTooltip(partyType: string | number): string {
  return PARTY_TYPE_CONFIG[String(partyType)]?.tooltip ?? "Onbekend";
}

const showMenuItemMjop = computed(() => userStore.hasRoleDmjopPowerUser);

// Menu item visibility for different party types
const showMeasures = computed(() => userStore.isPartyUtility);
const showBeheer = computed(() => showMenuItemBeheer.value && (showMenuItemInformatieplicht.value || showMenuItemInfo.value)); // Show only if there are visible items
const showMjop = computed(() => userStore.isPartyHome && showMenuItemMjop.value && false); // Disabled for homes



// Status page
interface StatusComponent {
  id: string;
  name: string;
  status: string;
  updatedAt: string;
  description?: string;
  groupId?: string;
}

const showStatusDialog = ref(false);
const statusComponents = ref<StatusComponent[]>([]);
const statusLoading = ref(false);

const overallStatus = computed(() => {
  const statusPriority: Record<string, number> = {
    "major_outage": 4,
    "down": 4,
    "partial_outage": 3,
    "degraded_performance": 2,
    "under_maintenance": 1,
    "operational": 0
  };

  if (statusComponents.value.length === 0) return "operational";

  let worstStatus = "operational";
  let maxPriority = 0;

  for (const component of statusComponents.value) {
    const priority = statusPriority[component.status] || 0;
    if (priority > maxPriority) {
      maxPriority = priority;
      worstStatus = component.status;
    }
  }

  return worstStatus;
});

function getStatusDotColor() {
  switch (overallStatus.value) {
    case "operational": return "#4caf50";
    case "degraded_performance":
    case "partial_outage": return "#ff9800";
    case "major_outage":
    case "down": return "#f44336";
    case "under_maintenance": return "#2196f3";
    default: return "#9e9e9e";
  }
}

function getComponentColor(status: string) {
  switch (status) {
    case "operational": return "#4caf50";
    case "degraded_performance":
    case "partial_outage": return "#ff9800";
    case "major_outage":
    case "down": return "#f44336";
    case "under_maintenance": return "#2196f3";
    default: return "#9e9e9e";
  }
}

function getComponentIcon(status: string) {
  switch (status) {
    case "operational": return "mdi-check-circle";
    case "under_maintenance": return "mdi-wrench";
    case "degraded_performance":
    case "partial_outage": return "mdi-alert-circle";
    case "major_outage":
    case "down": return "mdi-close-circle";
    default: return "mdi-help-circle";
  }
}

function getTranslation(status: string) {
  switch (status) {
    case "operational": return "Operationeel";
    case "under_maintenance": return "Onderhoud";
    case "degraded_performance": return "Verminderd";
    case "partial_outage": return "Ged. storing";
    case "major_outage": return "Grote storing";
    case "down": return "Uitgevallen";
    default: return status;
  }
}

async function fetchStatusComponents() {
  try {
    statusLoading.value = true;
    const response = await getStatusPageComponents();
    if (response.severity <= severityCode.warning && response.data) {
      statusComponents.value = response.data;
    }
  } catch (err) {
    console.error("Error fetching status components:", err);
  } finally {
    statusLoading.value = false;
  }
}

onMounted(() => {
  fetchStatusComponents();
  const interval = setInterval(fetchStatusComponents, 5 * 60 * 1000);
  (() => clearInterval(interval));
});

watch(projectMenuOpen, async (isOpen) => {
  if (!isOpen) {
    return;
  }

  await nextTick();
  
  // Use setTimeout to ensure DOM is stable
  setTimeout(() => projectSearchField.value?.focus(), 50);
});
</script>

<template>
  <v-overlay :model-value="switchingParty" class="d-flex align-center justify-center" persistent>
    <v-progress-circular indeterminate size="64" color="primary" />
    <span class="ml-4">Project wordt geladen...</span>
  </v-overlay>
  <v-app-bar app color="white" class="navbar">
    <template v-slot:prepend>
      <v-app-bar-nav-icon @click="drawerLeft = !drawerLeft" class="d-flex d-lg-none"></v-app-bar-nav-icon>
      <router-link :to="{ name: 'Dashboard', params: { partyReference: userStore.currentPartyReference } }">
        <v-img alt="INNAX Logo" class="shrink mr-2 brand" contain :src="logoInnax" />
      </router-link>
      <template class="d-none d-lg-flex">
        <v-btn variant="text" :to="{ name: 'Dashboard', params: { partyReference: userStore.currentPartyReference } }">
          Dashboard</v-btn
        >
        <v-btn v-if="userStore.isPartyUtility" variant="text" :to="{ name: 'BuildingOverview', params: { partyReference: userStore.currentPartyReference } }">Gebouwen</v-btn>
        <v-btn v-else-if="userStore.isPartyHome" variant="text" :to="{ name: 'ComplexOverview', params: { partyReference: userStore.currentPartyReference } }">Complexen</v-btn>
        <v-btn v-if="showMeasures" variant="text" :to="{ name: 'AppliedOverview', params: { partyReference: userStore.currentPartyReference } }">Maatregelen</v-btn>
        <v-btn v-if="userStore.isPartyUtility || userStore.isPartyHome" variant="text" :to="{ name: 'RoadMapUtility', params: { partyReference: userStore.currentPartyReference } }" v-show="userStore.isPartyUtility">Routekaart</v-btn>
        <v-btn v-if="userStore.isPartyHome" variant="text" :to="{ name: 'RoadMapHome', params: { partyReference: userStore.currentPartyReference } }">Routekaart</v-btn>
        <v-btn v-if="userStore.isPartyUtility || userStore.isPartyHome" variant="text" :to="{ name: 'Rapportage', params: { partyReference: userStore.currentPartyReference } }">Rapportage</v-btn>
        <v-btn v-if="showMjop" variant="text" :to="{ name: 'DMJOP dashboard', params: { partyReference: userStore.currentPartyReference } }">MJOP</v-btn>
        <v-menu v-if="showBeheer">
          <template v-slot:activator="{ props }">
            <v-btn variant="text" v-bind="props">
              Beheer
              <template #append>
                <v-icon>{{ props["aria-expanded"] === "true" ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              </template>
            </v-btn>
          </template>
          <v-list nav>
            <v-list-item v-if="showMenuItemInformatieplicht" variant="text" :to="{ name: 'ProjectCreateBulkUploadXML', params: { partyReference: userStore.currentPartyReference } }" title="Informatieplicht"> </v-list-item>
            <v-divider></v-divider>
            <v-list-item v-if="showMenuItemInfo" :to="{ name: 'Info' }" title="Info" variant="text" prepend-icon="mdi-information-outline"> </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </template>
    <template v-slot:append>
      <CommandSearchMenu />
      <v-menu
        v-if="userStore.getAllProjecs.length > 1"
        v-model="projectMenuOpen"
        location="bottom"
        :close-on-content-click="false"
        @update:modelValue="(open) => !open && (projectSearch = '')"
      >
        <template v-slot:activator="{ props }">
          <v-btn variant="outlined" rounded="lg" class="project-switcher-activator" v-bind="props">
            <v-icon v-if="partyIcon(userStore.currentPartyType)" start>{{ partyIcon(userStore.currentPartyType) }}</v-icon>
            {{ userStore.currentProjectName }}
            <template #append>
              <v-icon>{{ props["aria-expanded"] === "true" ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
            </template>
          </v-btn>
        </template>
        <v-sheet class="project-switcher-menu" rounded="lg" @click.stop>
          <v-text-field
            ref="projectSearchField"
            v-model="projectSearch"
            prepend-inner-icon="mdi-magnify"
            placeholder="Zoek naar partijen..."
            density="comfortable"
            variant="outlined"
            hide-details
            class="ma-3"
          ></v-text-field>

          <v-list class="navbar projects" density="compact">
            <v-list-item
              v-for="project in projectList"
              v-bind:value="project.reference"
              :key="project.reference"
              :disabled="switchingParty"
              :active="project.isCurrent"
              rounded="lg"
              link
              @click="switchProject(project.reference)"
            >
            <v-list-item-title>
              {{ project.name }}
            </v-list-item-title>
            <template v-slot:prepend>
              <v-tooltip v-if="partyIcon(project.partyType)" location="top" :text="partyTypeTooltip(project.partyType)">
                <template #activator="{ props }">
                  <v-icon v-bind="props">{{ partyIcon(project.partyType) }}</v-icon>
                </template>
              </v-tooltip>
            </template>
            <template v-slot:append>
              <v-icon v-if="project.isCurrent" icon="mdi-check"></v-icon>
              <v-tooltip v-else-if="project.isManager" location="top" text="Beheerder">
                <template #activator="{ props }">
                  <v-icon v-bind="props" icon="mdi-account-star"></v-icon>
                </template>
              </v-tooltip>
            </template>
            </v-list-item>

            <v-list-item v-if="projectList.length === 0" :disabled="true" rounded="lg">
              <v-list-item-title>Geen partijen gevonden</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-sheet>
      </v-menu>
      <div v-else class="v-btn">
        {{ userStore.currentProjectName }}
      </div>
      <template class="d-none d-md-flex">
        <div class="logo-project">
          <img :src="logoName" alt="project" />
        </div>
      </template>
      <template class="d-none d-lg-flex">
        <v-menu location="bottom end">
          <template v-slot:activator="{ props }">
            <v-btn variant="outlined" rounded="lg" class="account-menu-activator ml-3" v-bind="props">
              <v-icon>mdi-account-outline</v-icon>
            </v-btn>
          </template>
          <v-list class="navbar account-menu-list" density="compact">
            <v-list-subheader class="account-menu-list__header">Mijn account</v-list-subheader>
            <v-list-item class="account-menu-list__item" prepend-icon="mdi-account-outline" title="Account informatie" :to="{ name: 'AccountSettings', params: { partyReference: userStore.currentPartyReference } }"> </v-list-item>
            <v-divider class="account-menu-list__divider my-1"></v-divider>
            <v-list-item class="account-menu-list__item" link prepend-icon="mdi-logout" title="Uitloggen" @click="logout()"> </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <v-btn
        variant="outlined"
        rounded="lg"
        class="account-menu-activator ml-3 d-flex d-lg-none"
        @click="drawerRight = !drawerRight"
      >
        <v-icon>mdi-account-outline</v-icon>
      </v-btn>
    </template>
  </v-app-bar>

  <v-navigation-drawer v-model="drawerLeft" absolute temporary>
    <v-list nav>
      <v-list-item variant="text" :to="{ name: 'Dashboard', params: { partyReference: userStore.currentPartyReference } }">
        <v-list-item-title class="app-nav-list__title">Dashboard</v-list-item-title>
      </v-list-item>
      <v-divider></v-divider>
      <v-list-item v-if="userStore.isPartyUtility" variant="text" :to="{ name: 'BuildingOverview', params: { partyReference: userStore.currentPartyReference } }">
        <v-list-item-title class="app-nav-list__title">Gebouwen</v-list-item-title>
      </v-list-item>
      <v-list-item v-else-if="userStore.isPartyHome" variant="text" :to="{ name: 'ComplexOverview', params: { partyReference: userStore.currentPartyReference } }">
        <v-list-item-title class="app-nav-list__title">Complexen</v-list-item-title>
      </v-list-item>
      <v-list-item v-if="showMeasures" variant="text" :to="{ name: 'AppliedOverview', params: { partyReference: userStore.currentPartyReference } }">
        <v-list-item-title class="app-nav-list__title">Maatregelen</v-list-item-title>
      </v-list-item>
      <v-list-item v-if="userStore.isPartyUtility" variant="text" :to="{ name: 'RoadMapUtility', params: { partyReference: userStore.currentPartyReference } }">
        <v-list-item-title class="app-nav-list__title">Routekaart</v-list-item-title>
      </v-list-item>
      <v-list-item v-if="userStore.isPartyHome" variant="text" :to="{ name: 'RoadMapHome', params: { partyReference: userStore.currentPartyReference } }">
        <v-list-item-title class="app-nav-list__title">Routekaart</v-list-item-title>
      </v-list-item>
      <v-list-item v-if="userStore.isPartyUtility || userStore.isPartyHome" variant="text" :to="{ name: 'Rapportage', params: { partyReference: userStore.currentPartyReference } }">
        <v-list-item-title class="app-nav-list__title">Rapportage</v-list-item-title>
      </v-list-item>
      <v-divider></v-divider>

      <v-list-group v-if="showBeheer" :value="false">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" title="Beheer"></v-list-item>
        </template>
        <v-list-item v-if="showMenuItemInformatieplicht" variant="text" :to="{ name: 'ProjectCreateBulkUploadXML', params: { partyReference: userStore.currentPartyReference } }" title="Informatieplicht"> </v-list-item>
        <v-divider></v-divider>
        <v-list-item v-if="showMenuItemInfo" :to="{ name: 'Info' }" title="Info" prepend-icon="mdi-information-outline"></v-list-item>
      </v-list-group>
    </v-list>
  </v-navigation-drawer>
  <v-navigation-drawer v-model="drawerRight" temporary location="end">
    <v-list nav>
      <v-list-subheader>Mijn account</v-list-subheader>
      <!-- Uncomment this after IDO-7878 and IDO-7879 are done to show the account information -->
      <!-- <v-list-item prepend-icon="mdi-account-outline" title="Account informatie" :to="{ name: 'AccountSettings', params: { partyReference: userStore.currentPartyReference } }"> </v-list-item> -->
      <v-divider></v-divider>
      <v-list-item link @click="logout()" prepend-icon="mdi-logout" title="Uitloggen"> </v-list-item>
    </v-list>
  </v-navigation-drawer>

  <v-main>
    <inn-breadcrumbs v-if="showGlobalBreadcrumb" :items="breadcrumbs"></inn-breadcrumbs>
    <router-view></router-view>
  </v-main>
  <v-footer app fixed class="copyright">
    <span>&copy; {{ new Date().getFullYear() }} - Paris Proof Monitor 
   
    <v-tooltip v-if="statusComponents.length > 0" :text="`Applicatie status: ${getTranslation(overallStatus)}`" location="top">
      <template v-slot:activator="{ props }">
        <button 
          class="status-dot-button"
          v-bind="props"
          @click="showStatusDialog = true"
        >
          <span class="status-dot" :style="{ backgroundColor: getStatusDotColor() }"></span>
        </button>
      </template>
    </v-tooltip>
    </span>
  </v-footer>
  <status-dialog
    v-model="showStatusDialog"
    :components="statusComponents"
    :is-loading="statusLoading"
  ></status-dialog>
</template>

<style scoped>

</style>
